import type { AccountMeta, PublicKey, TransactionInstruction } from '@solana/web3.js';
import {
  CP_SWAP_PROGRAM_ID,
  MORFI_OPINION_PROGRAM_ID,
  SELL_CAP_PROGRAM,
} from '../constants/programs.js';

export const SPONSORSHIP_INSTRUCTION_NAMES = [
  'back',
  'buy',
  'sell',
  'boostMarket',
  'claimPrize',
  'claimBackerFees',
  'createMarket',
  'createTimedMarket',
  'stageOutcomeMetadata',
  'prepareOutcomes',
  'initPosition',
  'swapBaseInput',
] as const;

export type SponsorshipInstructionName = (typeof SPONSORSHIP_INSTRUCTION_NAMES)[number];

type SponsorshipProgram = 'markets' | 'cpSwap';

interface SponsorshipInstructionSpec {
  readonly idlInstructionName: string;
  readonly program: SponsorshipProgram;
  /** Exists only in the sell-cap Markets program. */
  readonly sellCapOnly?: true;
}

const SPECS: Readonly<Record<SponsorshipInstructionName, SponsorshipInstructionSpec>> = {
  back: { idlInstructionName: 'back', program: 'markets' },
  buy: { idlInstructionName: 'buy', program: 'markets' },
  sell: { idlInstructionName: 'sell', program: 'markets' },
  boostMarket: { idlInstructionName: 'boost_market', program: 'markets' },
  claimPrize: { idlInstructionName: 'claim_prize', program: 'markets' },
  claimBackerFees: { idlInstructionName: 'claim_backer_fees', program: 'markets' },
  createMarket: { idlInstructionName: 'create_market', program: 'markets' },
  createTimedMarket: { idlInstructionName: 'create_timed_market', program: 'markets' },
  stageOutcomeMetadata: { idlInstructionName: 'stage_outcome_metadata', program: 'markets' },
  prepareOutcomes: { idlInstructionName: 'prepare_outcomes', program: 'markets' },
  initPosition: { idlInstructionName: 'init_position', program: 'markets', sellCapOnly: true },
  swapBaseInput: { idlInstructionName: 'swap_base_input', program: 'cpSwap' },
};

export interface SponsorshipInstructionLayout {
  readonly name: SponsorshipInstructionName;
  readonly program: SponsorshipProgram;
  readonly programId: PublicKey;
  readonly discriminator: readonly number[];
  readonly accountNames: readonly string[];
  readonly accountCount: number;
  accountIndex(name: string): number | null;
}

export interface DecodedSponsorshipInstruction {
  readonly layout: SponsorshipInstructionLayout;
  readonly instruction: TransactionInstruction;
  readonly remainingAccounts: readonly AccountMeta[];
  account(name: string): AccountMeta | null;
}

type SponsorshipLayouts = ReadonlyMap<SponsorshipInstructionName, SponsorshipInstructionLayout>;

interface SponsorshipIdl {
  readonly address: string;
  readonly instructions: readonly SponsorshipIdlInstruction[];
}

interface SponsorshipIdlInstruction {
  readonly name: string;
  readonly discriminator: readonly number[];
  readonly accounts: readonly SponsorshipIdlAccount[];
}

interface SponsorshipIdlAccount {
  readonly name: string;
}

const layoutsByDeployment: Map<string, Promise<SponsorshipLayouts>> = new Map();

/**
 * Loads protocol instruction layouts directly from the committed Markets
 * (classic or sell-cap variant, per `SELL_CAP_PROGRAM`) and CP-swap IDLs,
 * bound to the configured program ids. Sponsorship policies address accounts
 * by name, never by a copied discriminator or numeric position.
 */
export async function getSponsorshipInstructionLayout(
  name: SponsorshipInstructionName,
): Promise<SponsorshipInstructionLayout> {
  const layouts = await loadSponsorshipLayouts(SPECS[name].program);
  const layout = layouts.get(name);
  if (layout === undefined) {
    throw new Error(`Missing sponsorship layout for ${name}`);
  }
  return layout;
}

/**
 * Decodes an instruction only when its program ID and discriminator match a
 * supported protocol layout. Extra accounts are preserved as remaining accounts.
 */
export async function decodeSponsorshipInstruction(
  instruction: TransactionInstruction,
): Promise<DecodedSponsorshipInstruction | null> {
  const program = instruction.programId.equals(CP_SWAP_PROGRAM_ID)
    ? 'cpSwap'
    : instruction.programId.equals(MORFI_OPINION_PROGRAM_ID)
      ? 'markets'
      : null;
  if (program === null) return null;
  const layouts = await loadSponsorshipLayouts(program);
  for (const layout of layouts.values()) {
    if (!instruction.programId.equals(layout.programId)) continue;
    if (!hasDiscriminator(instruction.data, layout.discriminator)) continue;
    if (instruction.keys.length < layout.accountCount) return null;
    return decodedInstruction(layout, instruction);
  }
  return null;
}

async function loadSponsorshipLayouts(program: SponsorshipProgram): Promise<SponsorshipLayouts> {
  // The deployment is live config (`configureChainAddresses`), so it keys the cache.
  const programId = program === 'markets' ? MORFI_OPINION_PROGRAM_ID : CP_SWAP_PROGRAM_ID;
  const sellCap = program === 'markets' && SELL_CAP_PROGRAM;
  const key = `${program}:${programId.toBase58()}:${String(sellCap)}`;
  const existing = layoutsByDeployment.get(key);
  if (existing !== undefined) return existing;
  const layouts = createSponsorshipLayouts(program, programId, sellCap);
  layoutsByDeployment.set(key, layouts);
  return layouts;
}

async function createSponsorshipLayouts(
  program: SponsorshipProgram,
  programId: PublicKey,
  sellCap: boolean,
): Promise<SponsorshipLayouts> {
  const idl: SponsorshipIdl = await loadSponsorshipIdl(program, sellCap);
  const layouts = new Map<SponsorshipInstructionName, SponsorshipInstructionLayout>();
  for (const name of SPONSORSHIP_INSTRUCTION_NAMES) {
    const spec = SPECS[name];
    if (spec.program !== program || (spec.sellCapOnly === true && !sellCap)) continue;
    const instruction = idl.instructions.find(
      (candidate) => candidate.name === spec.idlInstructionName,
    );
    if (instruction === undefined) {
      throw new Error(`IDL missing sponsorship instruction ${spec.idlInstructionName}`);
    }
    layouts.set(
      name,
      layoutFromIdl(name, spec.program, programId, instruction.discriminator, instruction.accounts),
    );
  }
  return layouts;
}

async function loadSponsorshipIdl(
  program: SponsorshipProgram,
  sellCap: boolean,
): Promise<SponsorshipIdl> {
  if (program === 'cpSwap') {
    return (await import('../../idl/raydium_cp_swap.json', { with: { type: 'json' } })).default;
  }
  return sellCap
    ? (await import('../../idl/markets.sell-cap.json', { with: { type: 'json' } })).default
    : (await import('../../idl/markets.json', { with: { type: 'json' } })).default;
}

function layoutFromIdl(
  name: SponsorshipInstructionName,
  program: SponsorshipProgram,
  programId: PublicKey,
  discriminator: readonly number[],
  accounts: readonly SponsorshipIdlAccount[],
): SponsorshipInstructionLayout {
  const accountNames = accounts.map((account) => account.name);
  const indexes = new Map(accountNames.map((accountName, index) => [accountName, index]));
  return {
    name,
    program,
    programId,
    discriminator: Object.freeze([...discriminator]),
    accountNames: Object.freeze(accountNames),
    accountCount: accountNames.length,
    accountIndex(accountName: string): number | null {
      return indexes.get(accountName) ?? null;
    },
  };
}

function decodedInstruction(
  layout: SponsorshipInstructionLayout,
  instruction: TransactionInstruction,
): DecodedSponsorshipInstruction {
  return {
    layout,
    instruction,
    remainingAccounts: instruction.keys.slice(layout.accountCount),
    account(name: string): AccountMeta | null {
      const index = layout.accountIndex(name);
      return index === null ? null : (instruction.keys[index] ?? null);
    },
  };
}

function hasDiscriminator(data: Uint8Array, discriminator: readonly number[]): boolean {
  if (data.length < discriminator.length) return false;
  return discriminator.every((byte, index) => data[index] === byte);
}
